import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { formatUSD, shortAddr } from "../lib/format";

export default function Wallet() {
    const { user } = useAuth();
    const [w, setW] = useState(null);
    const [txs, setTxs] = useState([]);
    const [depositAmount, setDepositAmount] = useState("1000");
    const [withdrawAmount, setWithdrawAmount] = useState("100");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const [bal, tx] = await Promise.all([api.get("/wallet/balance"), api.get("/transactions")]);
            setW(bal.data); setTxs(tx.data);
        } catch {}
    };
    useEffect(() => { load(); const id = setInterval(load, 3000); return () => clearInterval(id); }, []);

    const doAction = async (path, body, label) => {
        setBusy(true);
        try {
            await api.post(path, body);
            toast.success(`${label} successful`);
            load();
        } catch (e) {
            toast.error(`${label} failed`, { description: formatApiError(e.response?.data?.detail) || e.message });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-[#050505] min-h-[calc(100vh-3.5rem)] bg-grid-fine">
            <div className="max-w-5xl mx-auto px-4 lg:px-8 py-10 grid gap-6">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400">Wallet</div>
                    <h1 className="text-2xl sm:text-3xl font-medium tracking-tighter mt-2">Testnet USDC Treasury</h1>
                    <div className="mt-2 font-mono text-xs text-zinc-500 break-all" data-testid="wallet-address-display">
                        {user?.wallet_address && (
                            <>
                                Address: <span className="text-zinc-400">{shortAddr(user.wallet_address)}</span>
                                <span className="hidden md:inline"> · <span className="text-zinc-700">{user.wallet_address}</span></span>
                            </>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    <Card label="Deposited" value={w ? formatUSD(w.deposited) : "—"} testid="wallet-deposited" />
                    <Card label="Locked (margin)" value={w ? formatUSD(w.locked) : "—"} valueClass="text-amber-400" testid="wallet-locked" />
                    <div className="col-span-2 md:col-span-1">
                        <Card label="Free (available)" value={w ? formatUSD(w.free) : "—"} valueClass="text-cyan-400" testid="wallet-free" />
                    </div>
                </div>

                {/* Admin-only consolidated treasury: personal + all bot wallets */}
                {user?.role === "admin" && w?.treasury_total != null && (
                    <div className="bg-gradient-to-br from-cyan-500/5 via-violet-500/5 to-transparent border border-cyan-500/30 rounded-sm p-5" data-testid="admin-treasury-panel">
                        <div className="flex items-baseline justify-between flex-wrap gap-2">
                            <div>
                                <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-cyan-300">Admin · Consolidated treasury</div>
                                <div className="text-2xl sm:text-3xl font-medium tracking-tighter mt-1 font-mono tabular-nums" data-testid="admin-treasury-total">
                                    {formatUSD(w.treasury_total)}
                                </div>
                                <div className="text-[10px] font-mono text-zinc-500 mt-1">
                                    Personal wallet + total margin held across {w.bot_count || 0} liquidity bot{w.bot_count === 1 ? "" : "s"}
                                </div>
                            </div>
                            <a
                                href="/bots"
                                data-testid="admin-treasury-bots-link"
                                className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 hover:text-cyan-300 border border-cyan-500/30 px-3 py-1.5 rounded-sm"
                            >
                                Manage bots →
                            </a>
                        </div>
                        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
                            <MiniCard label="Personal" value={formatUSD(w.deposited)} />
                            <MiniCard label="Bot pool · deposited" value={formatUSD(w.bot_treasury_deposited)} accent="text-emerald-400" />
                            <MiniCard label="Bot pool · locked in matches" value={formatUSD(w.bot_treasury_locked)} accent="text-amber-400" />
                            <MiniCard label="Bots running" value={String(w.bot_count || 0)} accent="text-cyan-400" />
                        </div>
                    </div>
                )}

                {user?.is_vip_faucet && (
                    <div
                        data-testid="vip-treasury-card"
                        className="relative overflow-hidden bg-gradient-to-br from-cyan-500/15 via-cyan-500/5 to-transparent border border-cyan-400/40 rounded-sm p-6 shadow-[0_0_60px_-20px_rgba(34,211,238,0.4)]"
                    >
                        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-cyan-400/10 blur-3xl pointer-events-none" />
                        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div>
                                <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-300">
                                    VIP · Treasury access
                                </div>
                                <div className="text-2xl sm:text-3xl font-medium tracking-tighter mt-1">
                                    Claim <span className="text-cyan-300">$1,000,000,000</span> from Treasury
                                </div>
                                <div className="text-[11px] font-mono text-zinc-400 mt-2 max-w-lg leading-relaxed">
                                    Whitelisted account — request $1B per call, anytime. No lifetime cap. Click again to top up.
                                </div>
                            </div>
                            <button
                                data-testid="vip-claim-1b-button"
                                disabled={busy}
                                onClick={() => doAction(
                                    "/wallet/deposit",
                                    { amount: 1_000_000_000 },
                                    "Treasury claim — $1,000,000,000"
                                )}
                                className="shrink-0 px-6 py-3 bg-cyan-400 hover:bg-cyan-300 disabled:opacity-50 text-black font-semibold rounded-sm text-sm uppercase tracking-[0.18em] shadow-lg shadow-cyan-500/30 transition-all"
                            >
                                {busy ? "Claiming…" : "Claim $1B →"}
                            </button>
                        </div>
                    </div>
                )}

                <div className="grid md:grid-cols-2 gap-2">
                    <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm p-6">
                        <div className="flex items-center justify-between gap-2">
                            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">Faucet · Deposit</div>
                            {user?.is_vip_faucet && (
                                <span
                                    data-testid="wallet-vip-badge"
                                    className="text-[9px] font-mono uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm border border-cyan-400/60 bg-cyan-500/10 text-cyan-300"
                                >
                                    VIP · $1B/call
                                </span>
                            )}
                        </div>
                        <div className="text-lg font-medium mt-1">
                            {user?.is_vip_faucet
                                ? "Top up testnet USDC (max $1,000,000,000/call · unlimited lifetime)"
                                : "Top up testnet USDC (max 1M/call · 2M lifetime cap)"}
                        </div>
                        <input
                            data-testid="wallet-deposit-amount"
                            type="number" min={1}
                            max={user?.faucet_per_call_max || 1_000_000}
                            value={depositAmount}
                            onChange={(e) => setDepositAmount(e.target.value)}
                            className="mt-4 w-full bg-transparent border-b border-[#1F1F24] focus:border-cyan-400 outline-none py-2 font-mono"
                        />
                        <div className="mt-2 text-[10px] font-mono text-zinc-500">
                            {user?.is_vip_faucet
                                ? "Whitelisted account — request up to $1B per call, anytime."
                                : "Above 2M USDC lifetime requires admin approval — see Deposit Request below."}
                        </div>
                        <button
                            data-testid="wallet-deposit-button"
                            disabled={busy}
                            onClick={() => doAction("/wallet/deposit", { amount: parseFloat(depositAmount) }, "Deposit")}
                            className="mt-4 w-full bg-cyan-400 hover:bg-cyan-300 disabled:opacity-50 text-black font-semibold py-2.5 rounded-sm"
                        >
                            Deposit
                        </button>
                    </div>
                    <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm p-6">
                        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">Withdraw</div>
                        <div className="text-lg font-medium mt-1">Burn testnet USDC</div>
                        <input
                            data-testid="wallet-withdraw-amount"
                            type="number" min={1}
                            value={withdrawAmount}
                            onChange={(e) => setWithdrawAmount(e.target.value)}
                            className="mt-4 w-full bg-transparent border-b border-[#1F1F24] focus:border-cyan-400 outline-none py-2 font-mono"
                        />
                        <button
                            data-testid="wallet-withdraw-button"
                            disabled={busy}
                            onClick={() => doAction("/wallet/withdraw", { amount: parseFloat(withdrawAmount) }, "Withdraw")}
                            className="mt-4 w-full border border-[#1F1F24] hover:border-zinc-600 text-zinc-100 font-semibold py-2.5 rounded-sm"
                        >
                            Withdraw
                        </button>
                    </div>
                </div>

                <DepositRequestPanel />

                <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm">
                    <div className="px-4 py-3 border-b border-[#1F1F24]">
                        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">Recent transactions</div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[480px]" data-testid="transactions-table">
                        <thead>
                            <tr className="text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                                <th className="text-left px-4 py-2 font-normal">Type</th>
                                <th className="text-left px-4 py-2 font-normal">Amount</th>
                                <th className="text-left px-4 py-2 font-normal">When</th>
                            </tr>
                        </thead>
                        <tbody>
                            {txs.length === 0 && (
                                <tr><td colSpan={3} className="text-center py-6 text-[11px] font-mono text-zinc-600">no transactions yet</td></tr>
                            )}
                            {txs.map((t) => (
                                <tr key={t.id} className="border-t border-[#1F1F24] font-mono text-xs">
                                    <td className="px-4 py-1.5">{t.type}</td>
                                    <td className="px-4 py-1.5 text-zinc-300">{t.amount?.toLocaleString?.() || "—"}</td>
                                    <td className="px-4 py-1.5 text-zinc-500">{new Date(t.created_at).toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

const Card = ({ label, value, valueClass = "text-zinc-100", testid }) => (
    <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm p-5" data-testid={testid}>
        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">{label}</div>
        <div className={`mt-2 font-mono text-2xl tracking-tighter ${valueClass}`}>{value}</div>
    </div>
);

const MiniCard = ({ label, value, accent = "text-zinc-100" }) => (
    <div className="bg-[#050507] border border-[#1F1F24] rounded-sm p-3">
        <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-zinc-500">{label}</div>
        <div className={`mt-1 font-mono text-base tabular-nums ${accent}`}>{value}</div>
    </div>
);

function DepositRequestPanel() {
    const [amount, setAmount] = useState("1000000");
    const [reason, setReason] = useState("");
    const [my, setMy] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try { const r = await api.get("/wallet/deposit/requests"); setMy(r.data || []); }
        catch {}
    };
    useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, []);

    const submit = async () => {
        if (!amount || parseFloat(amount) <= 0) { toast.error("amount required"); return; }
        setBusy(true);
        try {
            await api.post("/wallet/deposit/request", { amount: parseFloat(amount), reason });
            toast.success("Request submitted — awaiting admin approval");
            setReason(""); load();
        } catch (e) {
            toast.error("submit failed", { description: formatApiError(e.response?.data?.detail) });
        } finally { setBusy(false); }
    };

    return (
        <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm p-6" data-testid="deposit-request-panel">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-cyan-400">Deposit request</div>
            <div className="text-base font-medium mt-1">Request a top-up beyond the auto-deposit cap</div>
            <p className="text-xs text-zinc-500 mt-1.5 max-w-xl">
                The auto-faucet lets you self-deposit up to $2,000,000 lifetime. Once you hit the cap,
                file a request here — an admin will review and credit (possibly with a fee).
            </p>
            <div className="mt-4 grid sm:grid-cols-2 gap-3 items-end">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500 mb-1">Amount USDC</div>
                    <input data-testid="deposit-request-amount" type="number" min={1} value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="w-full bg-transparent border-b border-[#1F1F24] focus:border-cyan-400 outline-none py-2 font-mono text-sm" />
                </div>
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500 mb-1">Reason (optional)</div>
                    <input data-testid="deposit-request-reason" value={reason}
                        onChange={(e) => setReason(e.target.value)} maxLength={500}
                        placeholder="e.g. running a 10M strategy backtest"
                        className="w-full bg-transparent border-b border-[#1F1F24] focus:border-cyan-400 outline-none py-2 font-mono text-sm" />
                </div>
            </div>
            <button data-testid="deposit-request-submit" disabled={busy} onClick={submit}
                className="mt-4 inline-flex bg-cyan-400 hover:bg-cyan-300 disabled:opacity-50 text-black font-semibold text-xs uppercase tracking-widest px-5 py-2.5 rounded-sm">
                {busy ? "Submitting…" : "Submit request"}
            </button>

            {my.length > 0 && (
                <div className="mt-6 grid gap-1.5">
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">Your requests</div>
                    {my.slice(0, 5).map((r) => (
                        <div key={r.id} className="flex items-center gap-3 text-[11px] font-mono py-1.5 border-b border-[#1F1F24]/60">
                            <span className={`px-1.5 py-0.5 rounded-sm text-[9px] uppercase tracking-[0.2em] ${
                                r.status === "PENDING" ? "bg-amber-500/10 text-amber-400" :
                                r.status === "APPROVED" ? "bg-emerald-500/10 text-emerald-400" :
                                "bg-rose-500/10 text-rose-400"
                            }`}>{r.status}</span>
                            <span className="text-zinc-500">{new Date(r.created_at).toLocaleString()}</span>
                            <span className="text-zinc-200">{Number(r.amount).toLocaleString()} USDC</span>
                            {r.status === "APPROVED" && r.credited_amount != null && (
                                <span className="text-emerald-400">→ credited {Number(r.credited_amount).toLocaleString()} (fee {r.fee_pct}%)</span>
                            )}
                            {r.status === "REJECTED" && r.review_note && (
                                <span className="text-rose-400 truncate">— {r.review_note}</span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
